<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import type { VideoItem } from '@/types'

const props = defineProps<{
	video: VideoItem
}>()

const emit = defineEmits<{
	close: []
}>()

const dialogEl = ref<HTMLElement | null>(null)

function onKeydown(e: KeyboardEvent) {
	if (e.key === 'Escape') {
		emit('close')
	}
}

onMounted(() => {
	document.addEventListener('keydown', onKeydown)
	document.body.style.overflow = 'hidden'
	dialogEl.value?.focus()
})

onUnmounted(() => {
	document.removeEventListener('keydown', onKeydown)
	document.body.style.overflow = ''
})

function onOverlayClick(e: MouseEvent) {
	if (e.target === e.currentTarget) {
		emit('close')
	}
}
</script>

<template>
	<Teleport to="body">
		<div
			class="vg:fixed vg:inset-0 vg:z-[9999] vg:flex vg:items-center vg:justify-center vg:bg-black/80 vg:p-4 md:vg:p-8"
			@mousedown="onOverlayClick"
		>
			<div
				ref="dialogEl"
				role="dialog"
				aria-modal="true"
				:aria-label="props.video.title"
				tabindex="-1"
				class="vg:relative vg:flex vg:max-h-[90vh] vg:w-full vg:max-w-4xl vg:flex-col vg:overflow-hidden vg:rounded-lg vg:bg-white vg:shadow-2xl vg:outline-none"
			>
				<button
					type="button"
					aria-label="Close"
					@click="emit('close')"
					class="vg:absolute vg:top-3 vg:right-3 vg:z-10 vg:flex vg:h-9 vg:w-9 vg:items-center vg:justify-center vg:rounded-full vg:bg-black/60 vg:text-white vg:transition hover:vg:bg-black/80"
				>
					<span aria-hidden="true" class="vg:text-xl vg:leading-none">&times;</span>
				</button>

				<div class="vg:w-full vg:bg-black">
					<video
						:src="props.video.videoUrl"
						controls
						autoplay
						playsinline
						class="vg:block vg:max-h-[60vh] vg:w-full"
					>
						Your browser does not support embedded video.
					</video>
				</div>

				<div class="vg:overflow-y-auto vg:px-6 vg:py-5">
					<h2 class="vg:mb-2 vg:text-2xl vg:font-semibold vg:text-gray-900">{{ props.video.title }}</h2>
					<div
						v-if="props.video.content"
						class="vg:space-y-3 vg:leading-relaxed vg:text-gray-600"
						v-html="props.video.content"
					/>
					<p v-else-if="props.video.excerpt" class="vg:text-gray-600">{{ props.video.excerpt }}</p>
				</div>
			</div>
		</div>
	</Teleport>
</template>
